<template>
  <div
    :class="['nudge-banner', variant, { clickable, 'with-action': !!actionLabel }]"
    :role="clickable ? 'button' : null"
    :tabindex="clickable ? 0 : null"
    @click="onBannerClick"
    @keydown.enter="onBannerClick"
  >
    <img v-if="iconSrc" :src="iconSrc" alt="" aria-hidden="true" class="nudge-banner__icon" />
    <div class="nudge-banner__message">
      <div v-if="title" class="nudge-banner__title">{{ title }}</div>
      <div class="nudge-banner__body"><slot /></div>
    </div>
    <button
      v-if="actionLabel"
      type="button"
      :class="['nudge-banner__action', actionStyleClass]"
      @click.stop="onActionClick"
    >
      {{ actionLabel }}
    </button>
  </div>
</template>

<script lang="ts">
import Vue from "vue";

/**
 * Mailbox-nudge banner — reusable surface for the "add mailbox" and
 * "pending accounts" nudges. Two interaction modes:
 *
 *  - clickable: the whole banner is the click target (e.g. cpWidget homescreen
 *    "add more mailboxes" nudge → opens Create).
 *  - with-action: a labelled button on the right (e.g. "Review pending
 *    details" on the pending-accounts banner).
 *
 * Slot the message content as default-slot children. The parent owns copy
 * and i18n so the same component drives every nudge surface.
 *
 * Intentionally NOT slotted into AppBanner.vue's priority enum — banner
 * priority handling is deferred.
 */
export default Vue.extend({
  name: "NudgeBanner",
  props: {
    /** Whole banner is clickable; emits `click` on press/Enter. */
    clickable: {
      type: Boolean,
      default: false,
    },
    /** When set, renders a CTA button on the right; emits `action`. */
    actionLabel: {
      type: String,
      default: "",
    },
    /** Visual variant — 'info' (default, blue) or 'promo' (emphasised). */
    variant: {
      type: String,
      default: "info",
      validator: (v: string) => ["info", "promo"].indexOf(v) > -1,
    },
    /** Optional bolder heading rendered above the slot/message body. */
    title: {
      type: String,
      default: "",
    },
    /**
     * Optional leading icon (path to an asset, e.g. `require('@/assets/info.svg')`
     * or a webpack import). Rendered before the message column. Decorative
     * only — `aria-hidden`/empty alt — so the visible text carries the
     * meaning for screen readers.
     */
    iconSrc: {
      type: String,
      default: "",
    },
    /**
     * Action button style:
     *   - 'outline' (default): transparent bg, themed border/text.
     *   - 'filled': uses the shared `.button` class so it inherits the
     *     partner-themed primary color (green for Titan/Name.com,
     *     blue for hostGator, etc.) — used for primary CTAs like
     *     "Create email account" / "Buy email account" on the iframe
     *     cards homescreen.
     *   - 'link': borderless underlined text-link — used for soft CTAs
     *     like "Review pending emails" where the banner itself carries
     *     most of the visual weight.
     */
    actionStyle: {
      type: String,
      default: "outline",
      validator: (v: string) => ["outline", "filled", "link"].indexOf(v) > -1,
    },
  },
  computed: {
    /**
     * One class per actionStyle variant. Only the 'filled' branch reaches
     * for the global `.button` class (so it picks up the theme's primary
     * color); the other two are styled below as `__action--outline` /
     * `__action--link` so they inherit the banner's override hooks.
     */
    actionStyleClass(): string {
      if (this.actionStyle === "filled") return "button";
      if (this.actionStyle === "link") return "nudge-banner__action--link";
      return "nudge-banner__action--outline";
    },
  },
  methods: {
    onBannerClick(e: Event): void {
      if (!this.clickable) return;
      this.$emit("click", e);
    },
    onActionClick(e: Event): void {
      this.$emit("action", e);
    },
  },
});
</script>

<style lang="scss" scoped>
// =============================================================================
// Override contract
// -----------------------------------------------------------------------------
// Vue 2 auto-merges a parent-passed `class` onto this component's single-root
// element. To make those classes actually win against the defaults below:
//
//   1. Defaults are wrapped in `:where()` so they have 0,0,0 specificity.
//      Any consumer class beats them — no `!important`, no specificity-
//      stacking, no fighting selectors.
//
//   2. The values most likely to be overridden (background, text, action
//      colors, spacing) are exposed as CSS custom properties on the root.
//      Consumers can override either by setting the variable from a passed
//      class, OR by writing a regular class rule. Pick whichever reads best.
//
// Examples (in a consumer's scoped stylesheet):
//
//   <nudge-banner class="my-banner" ... />
//
//   // CSS-vars path — clean, no specificity concerns at all:
//   .my-banner { --nudge-banner-bg: red; --nudge-banner-action-color: blue; }
//
//   // Regular class path — also works because defaults are :where()-wrapped:
//   .my-banner { background: red; padding: 24px 32px; }
// =============================================================================

:where(.nudge-banner) {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  padding: 14px 20px;
  margin-bottom: 24px;
  border-radius: 4px;
  background: var(--nudge-banner-bg, var(--lightBlueBgBanner));
  color: var(--nudge-banner-color, var(--primaryTextCol));
}

// Pending-accounts surface. Warm-cream default — design-locked
// across themes since the surrounding page chrome already carries the
// partner color. Override via `--nudge-banner-bg` from a consumer class
// when this banner needs to live on a non-default surface.
:where(.nudge-banner.promo) {
  background: var(--nudge-banner-bg, var(--warningCol2));
}

:where(.nudge-banner.clickable) {
  cursor: pointer;
  &:hover {
    filter: brightness(0.97);
  }
  &:focus {
    outline: 2px solid var(--linkBtnCol);
    outline-offset: 2px;
  }
}

:where(.nudge-banner__icon) {
  flex-shrink: 0;
  display: block;
}

:where(.nudge-banner__message) {
  flex: 1;
  font-weight: 600;
}

:where(.nudge-banner__title) {
  font-size: 15px;
  font-weight: 700;
  color: var(--primaryTextCol);
  margin-bottom: 4px;
}

:where(.nudge-banner__body) {
  font-weight: 500;
}

// Link variant — borderless underlined text in the themed link color.
// Used for soft CTAs like "Review pending emails" where the banner
// already carries the visual weight. Like the outline variant, this
// honours `--nudge-banner-action-color` so consumers can recolor
// without descendant selectors.
:where(.nudge-banner__action--link) {
  background: transparent;
  border: none;
  padding: 0;
  margin: 0;
  font: inherit;
  font-weight: 600;
  color: var(--nudge-banner-action-color, var(--linkBtnCol));
  text-decoration: none;
  cursor: pointer;
  white-space: nowrap;

  &:hover {
    text-decoration: underline;
    text-underline-offset: 2px;
  }
  &:focus {
    outline: 2px solid var(--linkBtnCol);
    outline-offset: 2px;
    border-radius: 2px;
  }
}

// Outline variant — scoped here so the alternative `actionStyle="filled"`
// path (which adds the global `.button` class) inherits the partner-
// themed primary color from the theme stylesheet instead of these rules.
:where(.nudge-banner__action--outline) {
  background: transparent;
  color: var(--nudge-banner-action-color, var(--linkBtnCol));
  border: 1px solid var(--nudge-banner-action-color, var(--linkBtnCol));
  border-radius: 4px;
  padding: 6px 14px;
  font-weight: 600;
  cursor: pointer;
  white-space: nowrap;

  &:hover {
    background: var(--nudge-banner-action-color, var(--linkBtnCol));
    color: var(--tabBgCol);
  }
  &:focus {
    outline: 2px solid var(--linkBtnCol);
    outline-offset: 2px;
  }
}
</style>
